import { colors as theme } from '../theme';
// Primary Movement Categories for exercise classification
export const MOVEMENT_CATEGORIES = [
  'Back', 'Shoulders', 'Chest', 'Arms', 'Legs', 'Core', 'Full Body', 'Cardio', 'Stretch'
];

// Single source of truth for movement-category colour coding — every chart
// that colours by muscle group (Muscle Group Split donut, Strength Progress
// bars, etc.) should read from this instead of keeping its own map, so a
// category always renders as the same colour everywhere on the page.
export const MOVEMENT_CATEGORY_COLORS = {
  Back: theme.gold,
  Chest: theme.success,
  Legs: theme.warning,
  Shoulders: theme.danger,
  Arms: '#0A84FF',
  Core: '#BF5AF2',
  'Full Body': theme.text,
  Cardio: '#64D2FF',
  Stretch: theme.textMuted,
  Other: theme.textMuted,
};

export const getMovementCategoryColor = (category) =>
  MOVEMENT_CATEGORY_COLORS[category] || MOVEMENT_CATEGORY_COLORS.Other;

// Secondary Muscles organized by Primary Movement Category
export const SECONDARY_MUSCLES = {
  Back: ['Lats', 'Upper Back', 'Lower Back', 'Traps', 'Rhomboids', 'Erector Spinae'],
  Shoulders: ['Front Delts', 'Side Delts', 'Rear Delts', 'Rotator Cuff'],
  Chest: ['Upper Chest', 'Lower Chest', 'Pectoralis Major'],
  Arms: ['Biceps', 'Triceps', 'Forearms', 'Brachialis'],
  Legs: ['Quadriceps', 'Hamstrings', 'Glutes', 'Hip Flexors', 'Adductors', 'Abductors',
         'Calves', 'Gastrocnemius', 'Soleus'],
  Core: ['Abs', 'Obliques', 'Transverse Abdominis', 'Rectus Abdominis', 'Serratus', 'Lower Back'],
  'Full Body': ['Multiple Muscle Groups'],
  Cardio: ['Cardiovascular', 'Endurance'],
  Stretch: ['Hip Flexors', 'Hamstrings', 'Quadriceps', 'Glutes', 'Calves', 'Lower Back', 'Upper Back', 'Chest', 'Shoulders', 'Full Body']
};

// Flattened array of all secondary muscles (deduplicated)
export const ALL_SECONDARY_MUSCLES = [...new Set(Object.values(SECONDARY_MUSCLES).flat())];

// Bodyweight Multipliers - Percentage of bodyweight actually moved in each exercise
// Based on biomechanics research for accurate volume tracking
export const BODYWEIGHT_MULTIPLIERS = {
  // Full bodyweight (100%) - Lifting entire body
  'Pull-ups': 1.0,
  'Chin-ups': 1.0,
  'Dips': 1.0,
  'Muscle-ups': 1.0,
  'Hanging Leg Raises': 1.0,

  // High bodyweight (70-90%) - Most of body weight
  'Decline Push-ups': 0.75,
  'Pike Push-ups': 0.70,
  'Handstand Push-ups': 0.90,

  // Medium bodyweight (50-70%) - Significant portion
  'Push-ups': 0.64,
  'Diamond Push-ups': 0.66,
  'Wide Push-ups': 0.62,
  'Inverted Rows': 0.60,
  'Body Rows': 0.60,

  // Partial bodyweight (30-50%) - Portion of upper/lower body
  'Decline Sit-ups': 0.45,
  'Leg Raises': 0.40,
  'Sit-ups': 0.35,
  'Crunches': 0.30,
  'Bicycle Crunches': 0.32,
  'V-ups': 0.50,

  // Lower bodyweight (20-30%) - Small portion or isometric
  'Plank': 0.20,
  'Side Plank': 0.20,
  'Dead Bug': 0.25,
};

// Quick preset multipliers for common exercise types
export const MULTIPLIER_PRESETS = [
  { label: '100% - Full BW (Pull-ups)', value: 1.0 },
  { label: '90% - Handstand Push-ups', value: 0.90 },
  { label: '75% - Decline Push-ups', value: 0.75 },
  { label: '64% - Standard Push-ups', value: 0.64 },
  { label: '60% - Inverted Rows', value: 0.60 },
  { label: '50% - V-ups', value: 0.50 },
  { label: '45% - Decline Sit-ups', value: 0.45 },
  { label: '40% - Leg Raises', value: 0.40 },
  { label: '35% - Sit-ups', value: 0.35 },
  { label: '30% - Crunches', value: 0.30 },
  { label: '20% - Planks', value: 0.20 },
];

// Muscle-to-body-part mapping for SVG visualization
// Maps muscle names to SVG path IDs in the body diagram
export const MUSCLE_TO_BODY_PART = {
  // Front view muscles
  'Chest': 'chest',
  'Upper Chest': 'chest',
  'Lower Chest': 'chest',
  'Pectoralis Major': 'chest',

  'Front Delts': 'front_delts',
  'Side Delts': 'side_delts',
  'Rotator Cuff': 'rotator_cuff',

  'Abs': 'abs',
  'Obliques': 'obliques',
  'Rectus Abdominis': 'abs',
  'Transverse Abdominis': 'abs',
  'Serratus': 'serratus',

  'Quadriceps': 'quads',

  'Biceps': 'biceps',
  'Brachialis': 'biceps',

  'Forearms': 'forearms',

  // Back view muscles
  'Back': 'back_all',
  'Lats': 'lats',
  'Upper Back': 'upper_back',
  'Traps': 'traps',
  'Rhomboids': 'upper_back',
  'Rear Delts': 'rear_delts',
  'Lower Back': 'lower_back',
  'Erector Spinae': 'lower_back',

  'Glutes': 'glutes',
  'Hamstrings': 'hamstrings',

  'Triceps': 'triceps',

  'Calves': 'calves',
  'Gastrocnemius': 'calves',
  'Soleus': 'calves',

  // Special cases
  'Shoulders': 'shoulders_all',
  'Arms': 'arms_all',
  'Legs': 'legs_all',
  'Core': 'core_all',
};
